/// <reference path="ReconciliationEntryState.ts" />
/// <reference path="BankAccountReconciliationHeader.ts" />
/// <reference path="BankMovementReconciliationCell.ts" />
/// <reference path="InvoiceLineReconciliationDataSource.ts" />

// The bank movements of the reconciliation list: a section per bank account with its head (a copy
// of the header template), each account drawn as a card.
class BankMovementReconciliationDataSource extends MIOObject
{
    private tableView:UITableView = null;
    private headerTemplate:BankAccountReconciliationHeader = null;
    private delegate:LedgerAccountReconciliationDataSourceDelegate = null;

    initWithTableView(tableView:UITableView, headerTemplate:BankAccountReconciliationHeader, delegate:LedgerAccountReconciliationDataSourceDelegate) {
        this.tableView = tableView;
        this.headerTemplate = headerTemplate;
        this.delegate = delegate;
    }

    private fromDate:string = null;
    private toDate:string = null;
    private statusFilter:ReconciliationStatusFilter = ReconciliationStatusFilter.all;
    private filterFormat:string = null;

    private selectedItem:BankMovement = null;

    reloadData(fromDate:string, toDate:string, statusFilter:ReconciliationStatusFilter, filterFormat:string) {
        this.fromDate = fromDate;
        this.toDate = toDate;
        this.statusFilter = statusFilter;
        this.filterFormat = filterFormat;
        this.selectedItem = null;

        this.tableView.dataSource = this;
        this.tableView.delegate = this;

        this.fetchedResultsController = null;
        this.tableView.reloadData();
    }

    numberOfSections(tableView:UITableView) {
        return this.fetchedResultsController?.sections.length ?? 0;
    }

    numberOfRowsInSection(tableView:UITableView, section:number) {
        let sec = this.fetchedResultsController.sections[section];
        return sec.numberOfObjects();
    }

    viewForHeaderInSection(tableView:UITableView, section:number) {
        let ip = MIOIndexPath.indexForRowInSection(0, section);
        let item = this.fetchedResultsController.objectAtIndexPath(ip) as BankMovement;

        let header = this.headerTemplate.copy() as BankAccountReconciliationHeader;
        header.awakeFromHTML();
        header.hidden = false;
        header.item = item.bankAccount;

        return header;
    }

    cellAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath) {
        let item = this.fetchedResultsController.objectAtIndexPath(indexPath) as BankMovement;

        let cell = tableView.dequeueReusableCellWithIdentifier("BankMovementCell") as BankMovementReconciliationCell;
        cell.item = item;
        cell.isLastInSection = indexPath.row == this.numberOfRowsInSection(tableView, indexPath.section) - 1;
        // a rebuilt list keeps the movement shown in the panel selected
        if (item == this.selectedItem) cell.selected = true;

        return cell;
    }

    didSelectCellAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath) {
        let item = this.fetchedResultsController.objectAtIndexPath(indexPath) as BankMovement;
        this.selectedItem = item;
        this.delegate?.didSelectItem(item);
    }

    /// The movement's cell shows its current state (after its entry changed).
    refreshItem(item:CoreAccounting) {
        if (!(item instanceof BankMovement) || this._fetchedResultsController == null) return;
        let ip = this._fetchedResultsController.indexPathForObject(item);
        if (ip == null) return;
        let cell = this.tableView.cellAtIndexPath(ip) as BankMovementReconciliationCell;
        if (cell != null) cell.item = item;
    }

    private _fetchedResultsController:MIOFetchedResultsController = null;
    set fetchedResultsController(value:MIOFetchedResultsController) {
        if (value == null && this._fetchedResultsController != null)
            this._fetchedResultsController.delegate = null;

        this._fetchedResultsController = value;
    }

    get fetchedResultsController():MIOFetchedResultsController {
        if (this._fetchedResultsController != null) return this._fetchedResultsController;

        let ad = MUIWebApplication.sharedInstance().delegate;

        let conditions = [];
        if (this.fromDate != null) {
            conditions.push("date >= '" + this.fromDate + "' AND date <= '" + this.toDate + "'");
        }
        if (this.filterFormat != null) conditions.push("(" + this.filterFormat + ")");

        let statusFormat = ReconciliationEntryStateHelper.predicateFormatForFilter(this.statusFilter);
        if (statusFormat != null) conditions.push("(" + statusFormat + ")");

        let pf = conditions.length > 0 ? conditions.join(" AND ") : null;

        let sds = [
            MIOSortDescriptor.sortDescriptorWithKey("bankAccount.info.iban", false),
            MIOSortDescriptor.sortDescriptorWithKey("date", false),
            MIOSortDescriptor.sortDescriptorWithKey("orderIndex", false)
        ];
        let fetchRequest = DBHelper.listFetchRequestWithEntityName("BankMovement", sds, pf);
        fetchRequest.relationshipKeyPathsForPrefetching = ["bankAccount.info", "bankAccount.bank", "bankAccount.ledgerAccount", "ledgerAccountBalance", "journalEntry", "analyticAccount", "documentLink.legalEntity"];

        let fetchedResultsController = new MIOFetchedResultsController();
        fetchedResultsController.initWithFetchRequest(fetchRequest, ad.managedObjectContext, "bankAccount.info.iban");
        fetchedResultsController.delegate = this;

        fetchedResultsController.performFetch();
        this._fetchedResultsController = fetchedResultsController;
        return this._fetchedResultsController;
    }

    // a saved movement rebuilds the list: the selection and the scroll position stay
    controllerDidChangeContent(controller:MIOFetchedResultsController) {
        let scrollTop = this.tableView.layer.scrollTop;
        this.tableView.reloadData();
        this.tableView.layer.scrollTop = scrollTop;
    }
}
